import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import axios from "./AxiosInstance.jsx";
import Boutton from "./Button.jsx";

const COUNTRY_COLORS = {
  "Zhongo": "#ff6b6b",
  "Eurocorp": "#3b82f6",
  "Petrolistan": "#f59e0b",
  "Volgovie": "#6b7280",
  "Autoristan": "#8b5cf6",
  "Sudica": "#f97316",
  "Neo Atlas": "#9ca3af",
  "Bricologia": "#10b981"
};

const AllStats = ({ gameState, mobile, setActiveStatView }) => {
  const [stats, setStats] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    axios.get("/stats/countries")
      .then(res => {
        setStats(res.data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Erreur chargement stats globales", err);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (gameState?.joueur?.short_name) {
      setSelectedCountries([gameState.joueur.short_name]);
    }
  }, [gameState]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-300 text-lg">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  const renderChart = (param, countriesData) => {
    const filteredCountriesData = Object.fromEntries(
      Object.entries(countriesData).filter(([country]) =>
        selectedCountries.includes(country)
      )
    );

    const years = Object.keys(
      Object.values(filteredCountriesData)[0] || {}
    ).sort();

    const chartData = years.map(year => {
      const entry = { year };
      for (const country in filteredCountriesData) {
        if (filteredCountriesData[country][year]) {
          entry[country] = filteredCountriesData[country][year].new_value;
        }
      }
      return entry;
    });

    return (
      <div key={param} className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-700/50 shadow-lg">
        <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center">
          <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mr-3"></div>
          {param}
        </h3>
        <div className="h-64 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" opacity={0.3} />
              <XAxis
                dataKey="year"
                stroke="#94a3b8"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#94a3b8"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '8px',
                  color: '#f1f5f9'
                }}
              />
              <Legend
                wrapperStyle={{ color: '#cbd5e1' }}
              />
              {Object.keys(filteredCountriesData).map((country) => (
                <Line
                  key={country}
                  type="monotone"
                  dataKey={country}
                  stroke={COUNTRY_COLORS[country]}
                  strokeWidth={3}
                  dot={{ fill: COUNTRY_COLORS[country], strokeWidth: 0, r: 4 }}
                  activeDot={{ r: 6, stroke: COUNTRY_COLORS[country], strokeWidth: 2, fill: '#1e293b' }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const allCountries = stats ? Object.keys(Object.values(stats)[0] || {}) : [];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header avec gradient */}
      <div className="flex justify-center space-x-4 p-4">
        <button
          onClick={() => { mobile === 'PC' ? navigate("/stats/countries") : setActiveStatView('general') }}
          className={`px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white`}
        >
          Impacts par année
        </button>
        <button
          onClick={() => { mobile === 'PC' ? navigate("/stats/ecoEnergy") : setActiveStatView('eco_energy') }}
          className={`px-4 py-2 rounded-lg text-sm font-medium bg-slate-700 text-slate-300 hover:bg-slate-600`}
        >
          Économie & Énergie
        </button>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Sélection des pays */}
        <div className="mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-700/50 shadow-lg">
            <h2 className="text-lg sm:text-xl font-semibold text-white mb-4 flex items-center">
              <span className="text-2xl mr-2">🌍</span>
              Sélectionner les pays
            </h2>

            {/* Boutons de sélection rapide */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setSelectedCountries(allCountries)}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium"
              >
                Tout sélectionner
              </button>
              <button
                onClick={() => setSelectedCountries([])}
                className="px-3 py-1.5 text-sm bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors duration-200 font-medium"
              >
                Tout désélectionner
              </button>
            </div>

            {/* Grille des pays */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {allCountries.map(country => (
                <label
                  key={country}
                  className={`
                    flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 group
                    ${selectedCountries.includes(country)
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                    }
                  `}
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      value={country}
                      checked={selectedCountries.includes(country)}
                      onChange={() => {
                        if (selectedCountries.includes(country)) {
                          setSelectedCountries(selectedCountries.filter(c => c !== country));
                        } else {
                          setSelectedCountries([...selectedCountries, country]);
                        }
                      }}
                      className="sr-only"
                    />
                    <div className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200
                      ${selectedCountries.includes(country)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-slate-400 group-hover:border-slate-300'
                      }
                    `}>
                      {selectedCountries.includes(country) && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="ml-3 flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: COUNTRY_COLORS[country] }}
                    ></div>
                    <span className="text-white font-medium text-sm">{country}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Graphiques */}
        {selectedCountries.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📈</div>
            <h3 className="text-xl font-semibold text-slate-300 mb-2">
              Aucun pays sélectionné
            </h3>
            <p className="text-slate-400">
              Sélectionnez au moins un pays pour voir les statistiques
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Object.entries(stats).map(([param, countriesData]) =>
              renderChart(param, countriesData)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllStats;